/**
 * ViewAppointmentPatientTab — Patient tab of the appointment view panel.
 *
 * Flutter parity: `view_edit_appointment/tab_widget/patient_tab.dart` —
 * a Personal / Contact sub-tab toggle with a per-tab "Save" button
 * (`patient_tab.dart:511-556`), gated by `Patient_Contacts`.
 *
 * This surface **composes the patient-module editable forms** rather than
 * re-implementing them — single source of truth with the standalone Edit
 * Patient screen (#521/#527/#528):
 *   - Personal sub-tab → `PersonalDetailsTab` (`src/patients/tabs/`)
 *   - Contact sub-tab  → `ContactDetailsTab`  (`src/patients/tabs/`)
 *
 * Each sub-tab gets its OWN `PatientEditShellProvider` + Save bar. Why not a
 * single shared provider around both: the two forms register their save
 * action via a `useEffect` whose cleanup nulls the shared value. When Radix
 * swaps the mounted TabsContent (Personal ⇄ Contact), the unmounting tab's
 * cleanup can run after the mounting tab's setup and null the freshly-set
 * action — the Save button then vanishes until reload (verified live). One
 * provider per sub-tab keeps each action isolated; only the active
 * TabsContent is mounted, so exactly one Save renders.
 *
 * Patient data: loaded via the canonical `usePatient(patientId)` (the shape
 * the reused forms expect). Flutter fills its `CreateEditPatientBloc` from
 * `POST /api/Practice/v1/PreLoadAppointmentPatient`; we reuse the
 * patients-module loader instead so the shared forms get exactly the body
 * they were built for. Documented divergence — behaviour-equivalent, and it
 * keeps the two surfaces from drifting. No new wire shape: save goes through
 * the same `useUpdatePatient` (`PUT /api/Patients/{id}`) the Edit Patient
 * screen uses (live-verified: PUT 200).
 *
 * On a successful save, `onSaved` invalidates `['appointment-details', …]` so
 * the panel's top strip (name / DOB / MRN / contact) reflects the edit.
 */

import { useCallback, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, SubTabs, type SubTabDef } from '@scripsteam/scrips-signal-ds';
import { usePermission } from '@/auth/permission-gate';
import { usePatient } from '@/patients/services/use-patient';
import {
  PatientEditShellProvider,
  usePatientEditShell,
} from '@/patients/components/patient-edit-shell-context';
import { PersonalDetailsTab } from '@/patients/tabs/personal-details-tab';
import { ContactDetailsTab } from '@/patients/tabs/contact-details-tab';
import { SavedStatus } from '@/patients/components/saved-status';

interface ViewAppointmentPatientTabProps {
  /** Patient to edit — from the appointment's patient participant. */
  patientId: string | null;
  /** Parent's appointment-details fetch is still in flight. */
  isLoading?: boolean;
  /** The page asking the user to complete the patient (see `PersonalDetailsTab.missingRequest`). Undefined = normal visit. */
  completeRequest?: number;
}

export function ViewAppointmentPatientTab({
  patientId,
  isLoading,
  completeRequest,
}: ViewAppointmentPatientTabProps) {
  const queryClient = useQueryClient();
  const canViewContacts = usePermission(['Patient_Contacts'], { view: true });
  const [activeSubTab, setActiveSubTab] = useState<'personal' | 'contact'>(
    'personal',
  );

  // Asked to complete the patient: show the Personal sub-tab, where the missing fields are.
  const [seenRequest, setSeenRequest] = useState(completeRequest);
  if (completeRequest !== seenRequest) {
    setSeenRequest(completeRequest);
    setActiveSubTab('personal');
  }

  // Full patient — the shape the reused forms expect (GET /api/Patients/{id}).
  const { data: patient, isLoading: patientLoading } = usePatient(patientId);

  // Refresh after a save: the appointment-details strip AND the patient itself.
  // `useUpdatePatient` marks `['patient', id]` stale with `refetchType: 'none'`
  // (no auto-refetch, to avoid a call-storm) — but this panel stays mounted
  // across saves, so we drive an explicit refetch here so the edited fields
  // re-bind immediately instead of showing stale data until a remount. Safe as
  // a single refetch: this is a stable panel, not the rapidly-unmounting modal
  // that PR #246 guarded against.
  const handleSaved = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['appointment-details'] });
    void queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
  }, [queryClient, patientId]);

  if (isLoading || patientLoading) {
    return (
      <div className="p-6 max-w-225 mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-grey-100 rounded w-1/3" />
          <div className="h-32 bg-grey-100 rounded" />
          <div className="h-32 bg-grey-100 rounded" />
        </div>
      </div>
    );
  }

  if (!patientId || !patient) {
    return (
      <div className="flex items-center justify-center h-full text-text-muted text-sm">
        No patient data available.
      </div>
    );
  }

  const subTabDefs: SubTabDef[] = [
    { id: 'personal', label: 'Personal', panelId: 'appt-patient-panel-personal' },
    ...(canViewContacts
      ? [{ id: 'contact', label: 'Contact', panelId: 'appt-patient-panel-contact' }]
      : []),
  ];

  const subTabs = (
    <SubTabs
      tabs={subTabDefs}
      value={activeSubTab}
      onValueChange={(v) => setActiveSubTab(v as 'personal' | 'contact')}
    />
  );

  return (
    <div className="flex flex-col h-full">
      {/* Sub-tab toggle (Flutter patient_tab.dart:511). The active sub-tab's own Save sits on the right end of
          the same row (per-tab Save — :522-556). */}
      {activeSubTab === 'personal' ? (
        <SubTabShell subTabs={subTabs} panelId="appt-patient-panel-personal" labelledBy="sub-tab-personal">
          <PersonalDetailsTab
            patient={patient}
            isCreateMode={false}
            hideInfoSummary
            onSaved={handleSaved}
            missingRequest={completeRequest}
          />
        </SubTabShell>
      ) : null}
      {activeSubTab === 'contact' && canViewContacts ? (
        <SubTabShell subTabs={subTabs} panelId="appt-patient-panel-contact" labelledBy="sub-tab-contact">
          <ContactDetailsTab patient={patient} onSaved={handleSaved} />
        </SubTabShell>
      ) : null}
    </div>
  );
}

/**
 * Wraps a sub-tab's form in its own `PatientEditShellProvider`: the sub-tab row (with the form's registered Save on its
 * right end) and the scrolling panel under it. One provider per sub-tab avoids the cross-tab save-action race (see
 * file header).
 */
function SubTabShell({
  subTabs,
  panelId,
  labelledBy,
  children,
}: {
  subTabs: ReactNode;
  panelId: string;
  labelledBy: string;
  children: ReactNode;
}) {
  return (
    <PatientEditShellProvider>
      <div className="appt-subtab-row">
        {subTabs}
        <SubTabSave />
      </div>
      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={labelledBy}
        className="flex-1 overflow-auto scrollbar-hide"
      >
        {children}
      </div>
    </PatientEditShellProvider>
  );
}

function SubTabSave() {
  const { saveAction } = usePatientEditShell();
  // Appears only once something on the form has changed.
  if (saveAction?.saved && saveAction.dirty === false) return <SavedStatus />;
  if (!saveAction || saveAction.dirty === false) return null;
  const label = saveAction.label === 'Save' ? 'Save changes' : saveAction.label;
  return (
    <Button
      type="button"
      size="sm"
      onClick={saveAction.onClick}
      disabled={saveAction.disabled || saveAction.busy}
      title={saveAction.disabled ? (saveAction.disabledReason ?? undefined) : undefined}
    >
      {saveAction.busy ? `${label}…` : label}
    </Button>
  );
}
